/**
 * Security Settings Component
 * Device management — view and revoke trusted devices
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import * as authService from '@/services/auth.service';
import type { TrustedDevice } from '@/types/api.types';

const SecuritySettings = () => {
  const queryClient = useQueryClient();
  const [confirmRevokeAll, setConfirmRevokeAll] = useState(false);
  const [revokeTargetId, setRevokeTargetId] = useState<string | null>(null);

  // Password change form
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState<string | null>(null);

  const changePasswordMutation = useMutation({
    mutationFn: () => authService.changePassword(currentPassword, newPassword),
    onSuccess: () => {
      toast.success('Паролата е сменена успешно');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setPasswordError(null);
    },
    onError: (err: Error) => {
      const message =
        err.message === 'Current password is incorrect'
          ? 'Текущата парола е грешна'
          : err.message || 'Грешка при смяна на паролата';
      setPasswordError(message);
      toast.error(message);
    },
  });

  const handleChangePassword = (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError(null);

    if (newPassword.length < 8) {
      setPasswordError('Новата парола трябва да е поне 8 символа');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('Паролите не съвпадат');
      return;
    }

    changePasswordMutation.mutate();
  };

  const { data: devices = [], isLoading, error } = useQuery<TrustedDevice[]>({
    queryKey: ['trusted-devices'],
    queryFn: authService.getDevices,
    staleTime: 30 * 1000,
  });

  const revokeDeviceMutation = useMutation({
    mutationFn: authService.revokeDevice,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['trusted-devices'] });
      setRevokeTargetId(null);
    },
  });

  const revokeAllMutation = useMutation({
    mutationFn: authService.revokeAllOtherDevices,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['trusted-devices'] });
      setConfirmRevokeAll(false);
    },
  });

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMin < 1) return 'Току-що';
    if (diffMin < 60) return `Преди ${diffMin} мин`;
    if (diffHours < 24) return `Преди ${diffHours} ч`;
    if (diffDays < 7) return `Преди ${diffDays} дни`;
    return date.toLocaleDateString('bg-BG', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const getDeviceIcon = (device: TrustedDevice) => {
    const os = (device.os || '').toLowerCase();
    const browser = (device.browser || '').toLowerCase();

    if (os.includes('android') || os.includes('ios')) return '📱';
    if (browser.includes('chrome')) return '🌐';
    if (browser.includes('firefox')) return '🦊';
    if (browser.includes('safari')) return '🧭';
    if (browser.includes('edge')) return '🔷';
    return '💻';
  };

  const otherDevicesCount = devices.filter(d => !d.is_current).length;

  return (
    <div className="space-y-6">
      {/* Section Header */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-dark-text-primary">
          Сигурност
        </h3>
        <p className="text-sm text-gray-500 dark:text-dark-text-muted mt-1">
          Управление на доверени устройства. Тези устройства не изискват код за верификация при вход.
        </p>
      </div>

      {/* Password Change */}
      <form
        onSubmit={handleChangePassword}
        className="space-y-4 p-4 rounded-lg border border-gray-200 dark:border-dark-border-primary bg-white dark:bg-dark-bg-secondary"
      >
        <h4 className="text-base font-semibold text-gray-900 dark:text-dark-text-primary">
          Смяна на парола
        </h4>

        {passwordError && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-300 px-4 py-2 rounded-lg text-sm">
            {passwordError}
          </div>
        )}

        <div>
          <label htmlFor="current-password" className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-1">
            Текуща парола
          </label>
          <input
            id="current-password"
            type="password"
            autoComplete="current-password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-dark-border-primary rounded-lg bg-white dark:bg-dark-bg-input text-gray-900 dark:text-dark-text-primary text-sm"
            required
          />
        </div>

        <div>
          <label htmlFor="new-password" className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-1">
            Нова парола
          </label>
          <input
            id="new-password"
            type="password"
            autoComplete="new-password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-dark-border-primary rounded-lg bg-white dark:bg-dark-bg-input text-gray-900 dark:text-dark-text-primary text-sm"
            required
            minLength={8}
          />
        </div>

        <div>
          <label htmlFor="confirm-password" className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-1">
            Потвърди новата парола
          </label>
          <input
            id="confirm-password"
            type="password"
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-dark-border-primary rounded-lg bg-white dark:bg-dark-bg-input text-gray-900 dark:text-dark-text-primary text-sm"
            required
            minLength={8}
          />
        </div>

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={changePasswordMutation.isPending}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium disabled:opacity-50"
          >
            {changePasswordMutation.isPending ? 'Изчакайте...' : 'Смени паролата'}
          </button>
        </div>
      </form>

      {/* Loading */}
      {isLoading && (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm">
          Грешка при зареждане на устройствата.
        </div>
      )}

      {/* Devices List */}
      {!isLoading && !error && (
        <div className="space-y-3">
          {devices.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-dark-text-muted py-4 text-center">
              Няма доверени устройства.
            </p>
          ) : (
            devices.map((device) => (
              <div
                key={device.id}
                className={`flex items-center justify-between p-4 rounded-lg border ${
                  device.is_current
                    ? 'border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-900/20'
                    : 'border-gray-200 dark:border-dark-border-primary bg-white dark:bg-dark-bg-secondary'
                }`}
              >
                <div className="flex items-center gap-4 min-w-0">
                  <span className="text-2xl flex-shrink-0">{getDeviceIcon(device)}</span>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-gray-900 dark:text-dark-text-primary truncate">
                        {device.device_name || 'Неизвестно устройство'}
                      </p>
                      {device.is_current && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-200 flex-shrink-0">
                          Текущо
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-dark-text-muted mt-0.5">
                      {device.ip_address && <span>{device.ip_address}</span>}
                      {device.ip_address && <span>·</span>}
                      <span>{formatDate(device.last_active)}</span>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                {!device.is_current && (
                  <div className="flex-shrink-0 ml-4">
                    {revokeTargetId === device.id ? (
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => revokeDeviceMutation.mutate(device.id)}
                          disabled={revokeDeviceMutation.isPending}
                          className="text-xs px-3 py-1.5 bg-red-500 text-white rounded hover:bg-red-600 disabled:opacity-50"
                        >
                          {revokeDeviceMutation.isPending ? '...' : 'Потвърди'}
                        </button>
                        <button
                          onClick={() => setRevokeTargetId(null)}
                          className="text-xs px-3 py-1.5 bg-gray-200 dark:bg-dark-bg-tertiary text-gray-700 dark:text-dark-text-secondary rounded hover:bg-gray-300"
                        >
                          Отказ
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setRevokeTargetId(device.id)}
                        className="text-xs px-3 py-1.5 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                      >
                        Излез
                      </button>
                    )}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}

      {/* Revoke All Others */}
      {otherDevicesCount > 0 && (
        <div className="pt-4 border-t border-gray-200 dark:border-dark-border-primary">
          {confirmRevokeAll ? (
            <div className="flex items-center justify-between bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
              <p className="text-sm text-red-800 dark:text-red-300">
                Сигурни ли сте? Ще излезете от {otherDevicesCount} {otherDevicesCount === 1 ? 'устройство' : 'устройства'}.
              </p>
              <div className="flex items-center gap-2 flex-shrink-0 ml-4">
                <button
                  onClick={() => revokeAllMutation.mutate()}
                  disabled={revokeAllMutation.isPending}
                  className="text-sm px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 disabled:opacity-50"
                >
                  {revokeAllMutation.isPending ? 'Изчакайте...' : 'Потвърди'}
                </button>
                <button
                  onClick={() => setConfirmRevokeAll(false)}
                  className="text-sm px-4 py-2 bg-gray-200 dark:bg-dark-bg-tertiary text-gray-700 dark:text-dark-text-secondary rounded hover:bg-gray-300"
                >
                  Отказ
                </button>
              </div>
            </div>
          ) : (
            <button
              onClick={() => setConfirmRevokeAll(true)}
              className="w-full text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg py-3 transition-colors"
            >
              Излез от всички други устройства ({otherDevicesCount})
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default SecuritySettings;
